import { createPortal } from "react-dom";
import { Exercise } from "../../types/conversations";
import utils from "../../helpers/utils";
import { WebViewCompatVideo } from "../ui/WebViewCompatVideo";
import { getVideoThumbnailUrl } from "../../services/videos";

const isVideoUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (url.startsWith("data:video")) return true;
  return [".mp4", ".webm", ".ogg", ".mov", ".m4v", ".avi", ".mkv", ".m3u8"].some(
    (ext) => url.toLowerCase().includes(ext),
  );
};

function getVideoSrc(exercise: Exercise): string | null {
  if (!exercise.video || String(exercise.video).trim() === "") return null;
  if (exercise.video.startsWith("data:video")) return exercise.video;
  const profId = exercise.professional_id
    ? String(exercise.professional_id)
    : "all_view";
  return utils.getUriImage(exercise.video, profId);
}

interface ExerciseViewModalProps {
  isOpen: boolean;
  exercise: Exercise | null;
  onClose: () => void;
}

export const ExerciseViewModal = ({
  isOpen,
  exercise,
  onClose,
}: ExerciseViewModalProps) => {
  if (!isOpen || !exercise) return null;

  const imageUrl = exercise.image
    ? utils.getUriImage(exercise.image, "all_view", { width: 480, height: 480 })
    : exercise.image_url || "";

  const videoSrc = getVideoSrc(exercise);
  const hasVideo = Boolean(videoSrc && isVideoUrl(videoSrc));

  return createPortal(
    <div
      className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto transition-all duration-300 p-2 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative w-full max-w-full sm:max-w-[600px] bg-white shadow-sm dark:bg-boxdark dark:border dark:border-strokedark !rounded-xl sm:!rounded-2xl p-4 sm:p-6 overflow-hidden">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute right-2 top-2 sm:right-4 sm:top-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="Fechar modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <div className="mb-4 pr-8 sm:pr-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">
            {exercise.name}
          </h3>
        </div>
        <div className="overflow-y-auto custom-scrollbar max-h-[85vh]">
          <div className="space-y-4">
            {/* Imagem do exercício */}
            {imageUrl && !hasVideo && (
              <div className="w-full h-64 sm:h-80 rounded-lg overflow-hidden">
                <img
                  src={imageUrl}
                  alt={exercise.name}
                  className="w-full object-contain"
                />
              </div>
            )}

            {/* Vídeo do exercício */}
            {hasVideo && videoSrc && (
              <div className="w-full rounded-lg overflow-hidden bg-black">
                <WebViewCompatVideo
                  src={videoSrc}
                  controls
                  preload="metadata"
                  className="w-full aspect-video object-contain"
                  poster={
                    imageUrl ||
                    getVideoThumbnailUrl(videoSrc) ||
                    undefined
                  }
                >
                  Seu navegador não suporta a reprodução de vídeos.
                </WebViewCompatVideo>
              </div>
            )}

            {/* Descrição */}
            {exercise.description && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {exercise.description}
                </p>
              </div>
            )}

            {/* Informações adicionais */}
            {exercise.all_view !== undefined && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Informações
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Visibilidade:</span>{" "}
                  {exercise.all_view ? "Público" : "Privado"}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
